// imports
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, mapActions } from '../store';
import { additionalLayers } from '../configs/additionalLayers';
import { getUrlParam } from '../utils/getUrlParam';

// Helper function to update URL without causing re-renders
const updateUrlParam = (key: string, value: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.history.replaceState({}, '', url.toString());
};

// Helper function to remove URL parameter
const removeUrlParam = (key: string) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(key);
  window.history.replaceState({}, '', url.toString());
};

export const useUrlParameters = () => {
  // Read URL parameters
  const getParameter = useCallback((key: string): string | null => {
    return getUrlParam(key);
  }, []);

  // Update URL parameters
  const setParameter = useCallback((key: string, value: string) => {
    updateUrlParam(key, value);
  }, []);

  // Get multiple parameters at once
  const getParameters = useCallback(
    (keys: string[]): Record<string, string | null> => {
      const result: Record<string, string | null> = {};
      keys.forEach((key) => {
        result[key] = getUrlParam(key);
      });
      return result;
    },
    []
  );

  // Set multiple parameters at once
  const setParameters = useCallback((params: Record<string, string>) => {
    const url = new URL(window.location.href);
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    window.history.replaceState({}, '', url.toString());
  }, []);

  return {
    getParameter,
    setParameter,
    getParameters,
    setParameters,
  };
};

// For layer URL management - call this in LayersAccordion
export const useLayerUrlSync = () => {
  const dispatch = useDispatch();
  const layers = useSelector((state: RootState) => state.map.layers);
  const initialized = useRef(false);
  const lastUrlValue = useRef<string | null>(null);

  // Initialize layers from URL on mount only
  useEffect(() => {
    if (!initialized.current) {
      const addlayersParam = getUrlParam('addlayers');
      if (addlayersParam) {
        const layerIds = addlayersParam
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id && additionalLayers[id]);

        layerIds.forEach((layerId) => {
          dispatch(mapActions.setActive({ id: layerId, active: true }));
        });
        lastUrlValue.current = addlayersParam;
      }
      initialized.current = true;
    }
  }, [dispatch]);

  // Update URL when layers change after initialization
  useEffect(() => {
    if (!initialized.current) return;

    // Get and alphabetize the layer ids
    const activeLayers = Object.entries(layers)
      .filter(([, layer]) => layer.active)
      .map(([id]) => id)
      .sort();

    let newValue: string | null;

    // If there are layers visible
    if (activeLayers.length > 0) {
      // Join the layer ids with comma separations
      newValue = activeLayers.join(',');
    } else {
      newValue = null;
    }

    // Only update if the value actually changed
    if (newValue !== lastUrlValue.current) {
      if (newValue) {
        updateUrlParam('addlayers', newValue);
      } else {
        removeUrlParam('addlayers');
      }
      lastUrlValue.current = newValue;
    }
  }, [layers]);
};
